import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import React, { useEffect, useMemo, useState } from 'react';
import ApiGo_TS from '../../../../actions/ApiGo_TS';
import HisMovenmentContent from './HisMovenmentContent';
import UiIntegrater from '../littleUIComponent/UiIntegrater';
import { ADD_RESPONSE } from '../../../../stores/reducer_add';
import { ADD_HIS_ALERTS } from '../../../../stores/reducer_HistoryData';
import { useAppDispatch, useAppSelector } from '../../../../stores/hooks';
import comm_imgs from '../../../../comm_imgs';
import { convertTimestamp } from './HisMobility';
import { useTranslation } from 'react-i18next';
import { convertDatestamp } from './HisMobilityByDays';

// 時區安全版本，強制台灣時間
const TZ = 'Asia/Taipei';
const toTWDateStr = (timestamp: any): string =>
  new Date(timestamp).toLocaleString('sv', { timeZone: TZ }).slice(0, 10);

const convertTimestampTW = (timestamp: any): string => {
  const dateStr = toTWDateStr(timestamp);
  const timeStr = new Date().toLocaleString('sv', { timeZone: TZ }).slice(11, 16);
  return `${dateStr} ${timeStr}`;
};

const convertDatestampTW = (timestamp: any) => {
  const [y, m, d] = toTWDateStr(timestamp).split('-').map(Number);
  const base = new Date(y, m - 1, d);
  const from = new Date(base); from.setDate(base.getDate() - 29);
  const fmt = (dt: Date) =>
    `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
  return { fromDate: fmt(from), endDate: fmt(base) };
};

export interface FormattedData {
  [category: string]: string[][];
}
interface DataState {
  isOldData: boolean;
  oldResult: any[];
  newResult: any;
  residentInfo?: any[];
}
const HisMovenment = (props: any): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const id: number = props.data.id;

  // ------- redux --------------------
  const [data, setData] = useState<DataState>({
    isOldData: false,
    oldResult: [],
    newResult: {},
    residentInfo: [],
  });
  
  const store_timestamp = useAppSelector((state) => state.store_historyData.timestamp);

  useEffect(() => {
    fetchData();
  }, [store_timestamp, props.days_open]);
  

  // 翻譯屬性名稱(用於生成Excel文件)
  const translations: { [key: string]: string } = useMemo(
    () => ({
      sitting_up: t('GLOBAL.ALERT_STATE_12'),
      sitting_on_bed_edge: t('HISTORY_RECORD.MOBILITY_EDGE'),
      leaving_bed: t('HISTORY_RECORD.MOBILITY_OUTOFBED'),
    }),
    [t]
  );

  // 更新版mobilityData
  const fetchData = async () => {
    if (store_timestamp.length === 0) return;
    if (props.days_open === 'TODAY') {
      const data = convertTimestampTW(store_timestamp);
      try {
        const res = await ApiGo_TS.getMobilityMovenment<FormattedData>(id, data);
        const filteredData = filterArrays(res);
        if (Object.keys(res).length === 1) {
          dispatch(ADD_RESPONSE({ status: 404, statusText: 'Not Found', type: 'hisRec_no_data' }));
          dispatch(ADD_HIS_ALERTS([]));
        }
        setData((pre) => ({
          ...pre,
          isOldData: false,
          newResult: filteredData,
          residentInfo: res?.residentInfo,
        }));
      } catch (error) {
        console.log('error', error);
      }
    } else {
      const data = convertDatestampTW(store_timestamp);
      try {
        const res = await ApiGo_TS.getMobilityMovenmentForDays<FormattedData>(id, data);
        const filteredData = filterArrays(res);
        if (Object.keys(res).length === 1) {
          dispatch(ADD_RESPONSE({ status: 404, statusText: 'Not Found', type: 'hisRec_no_data' }));
          dispatch(ADD_HIS_ALERTS([]));
        }
        setData((pre) => ({
          ...pre,
          isOldData: false,
          newResult: filteredData,
          residentInfo: res?.residentInfo,
        }));
      } catch (error) {
        console.log('error', error);
      }
    }
  };
  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();

      Object.entries(data.newResult).forEach(([key, value]) => {
        const [count, totalTime, dataArr] = value as [string, string, any[]];

        if (count === '0' && totalTime === '0:00:00' && dataArr.length === 0) {
          return;
        }

        const worksheet = workbook.addWorksheet(translations[key] || key);

        // 添加標題
        worksheet.mergeCells('A1:F1');
        const titleCell = worksheet.getCell('A1');
        titleCell.value = translations[key] || key;
        titleCell.font = { bold: true, size: 24 };
        titleCell.alignment = { vertical: 'middle', horizontal: 'center' };

        // 添加次數和總時間
        worksheet.mergeCells('A2:C2');
        worksheet.mergeCells('D2:F2');
        const countCell = worksheet.getCell('A2');
        countCell.value = `${t('GLOBAL.TIMES')}: ${count}`;
        countCell.font = { bold: true, size: 18 };
        countCell.alignment = { vertical: 'middle', horizontal: 'center' };
        const timeCell = worksheet.getCell('D2');
        timeCell.value = `${t('HISTORY_RECORD.MOBILITY_TOTAL_TIME')}: ${totalTime}`;
        timeCell.font = { bold: true, size: 18 };
        timeCell.alignment = { vertical: 'middle', horizontal: 'center' };
        // 添加表頭
        const headerRow = worksheet.addRow([t('GLOBAL.DATE'), t('HISTORY_RECORD.MOBILITY_START'), t('HISTORY_RECORD.MOBILITY_INITIAL'), t('HISTORY_RECORD.MOBILITY_PROCESS'), t('HISTORY_RECORD.MOBILITY_END'), t('HISTORY_RECORD.MOBILITY_SPENT')]);

        // 設置列寬
        worksheet.columns = [
          { key: 'date', width: 20 },
          { key: 'startTime', width: 20 },
          { key: 'start_action', width: 20 },
          { key: 'action_description', width: 35 },
          { key: 'endTime', width: 20 },
          { key: 'duration', width: 20 },
        ];

        // 添加數據
        dataArr.forEach((row: any) => {
          worksheet.addRow(row);
        });

        // 設置日期格式
        worksheet.getColumn('A').numFmt = 'yyyy-mm-dd';

        // 設置所有單元格居中對齊
        worksheet.eachRow((row, rowNumber) => {
          if (rowNumber > 2) {
            // 只針對內文
            row.eachCell((cell) => {
              cell.font = { size: 16 };
              cell.alignment = { vertical: 'middle', horizontal: 'center' };
            });
          }
        });
        headerRow.font = { bold: true, size: 16 };
        headerRow.eachCell((cell) => {
          cell.alignment = { vertical: 'middle', horizontal: 'center' };
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const residentInfo = data.residentInfo ?? ['', ''];
      const fileName = `${residentInfo[0]}_${residentInfo[1]}_${t('HISTORY_RECORD.MOBILITY_ANALYSIS')}.xlsx`;

      saveAs(new Blob([buffer], { type: 'application/octet-stream' }), fileName);
    } catch (error) {
      console.error('Error generating Excel file:', error);
    }
  };

  return (
    <>
      <UiIntegrater OnOff_switch={[true, false, false, true, true]} date={new Date()} name={props.data !== undefined ? props.data.last_name : ''} bed={props.data !== undefined ? props.data.first_name : ''} fordays={true} days_open={props.days_open} setDays_open={props.setDays_open} />
      {(!data.isOldData && Object.keys(data.newResult).length === 0) || (data.isOldData && data.oldResult[0].length === 0) ? (
        <div className="font-xl pad-30 t l">{t('GLOBAL.NO_DATA')}</div>
      ) : (
        <>
          {data.isOldData ? (
            <div className="fx-d-c h-85 pad-15 l">
              <div className="frame-table">
                {data.oldResult.map((res, index) => (
                  <div key={index}>{res}</div>
                ))}
              </div>
            </div>
          ) : (
            <div className="fx-d-c w-100 h-85 hisChart pos-r">
              <div className="pre_empty pos-a r-0" style={{ top: '10px' }}>
                <div className="w-30 fx-d-r fx-fec">
                  <div className="tag downloadData font-ml fx-ccc" onClick={exportToExcel}>
                    <div className="fx-d-r fx-ccc downloadShow" title={t('HISTORY_RECORD.MOBILITY_DOWNLOAD')}>
                      <div className="fx-ccc w-20">
                        <img src={comm_imgs.DB_INFO} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <HisMovenmentContent
                data={data.newResult}
                category=""
                selectedDate={toTWDateStr(store_timestamp)}
                days_open={props.days_open}
              />
            </div>
          )}
        </>
      )}
    </>
  );
};

export default HisMovenment;

// 過濾掉 residentInfo 資料 (用於生成Excel文件)
function filterArrays(data: any) {
  const filteredData: { [key: string]: any } = {};
  for (const key in data) {
    if (!key.endsWith('residentInfo')) {
      filteredData[key] = data[key];
    }
  }
  return filteredData;
}
